import { createAsyncThunk } from '@reduxjs/toolkit';
import { registrationService } from '../../services/api/registrationService';
import type { NewUserRegistration } from '../../types/registration';
import type { User, ApiError } from '../../types/index';
import { saveSensitiveData, getSensitiveData, removeSensitiveData } from '../../utils/secureStorage';

export const createUser = createAsyncThunk<
  User,
  NewUserRegistration,
  { rejectValue: ApiError }
>(
  'registration/createUser',
  async (userData: NewUserRegistration, { rejectWithValue }) => {
    try {
      const response = await registrationService.createUser(userData);
      
      if (response.token) {
        await saveSensitiveData('authToken', response.token);
      }
      
      return response.user;
    } catch (error: any) {
      const apiError: ApiError = {
        message: error.response?.data?.message || error.message || 'Failed to create user',
        code: error.response?.data?.code || 'CREATE_USER_ERROR',
      };
      return rejectWithValue(apiError);
    }
  }
);

export const resetRegistration = createAsyncThunk(
  'registration/reset',
  async () => {
    await removeSensitiveData('registrationDraft');
    return true;
  }
);

export const saveRegistrationDraft = createAsyncThunk<
  boolean,
  NewUserRegistration,
  { rejectValue: ApiError }
>(
  'registration/saveDraft',
  async (userData: NewUserRegistration, { rejectWithValue }) => {
    try {
      await saveSensitiveData('registrationDraft', JSON.stringify(userData));
      return true;
    } catch (error: any) {
      const apiError: ApiError = {
        message: 'Failed to save registration draft',
        code: 'SAVE_DRAFT_ERROR',
      };
      return rejectWithValue(apiError);
    }
  }
);

export const loadRegistrationDraft = createAsyncThunk<
  NewUserRegistration | null,
  void,
  { rejectValue: ApiError }
>(
  'registration/loadDraft',
  async (_, { rejectWithValue }) => {
    try {
      const draft = await getSensitiveData('registrationDraft');
      if (draft) {
        return JSON.parse(draft) as NewUserRegistration;
      }
      return null;
    } catch (error: any) {
      const apiError: ApiError = {
        message: 'Failed to load registration draft',
        code: 'LOAD_DRAFT_ERROR',
      };
      return rejectWithValue(apiError);
    }
  }
);
